import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';

import NavButton from '../NavButton';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 650,
  bgcolor: 'background.paper',
  borderRadius: '10px',
  boxShadow: 24
};

export interface ModalWidgetProps {
  isOpen: boolean;
  title: string;
  children: JSX.Element;
  onClose: () => void;
  modalWidth?: any;
  modalHeight?: any;
  positionTopRight?: boolean;
  styleObject?: React.CSSProperties;
  backgroundColor?: string;
  textColor?: string;
}

interface IconProps {
  icon: string;
  iconHover: string;
  value: string;
}

export const IconCell = ({ icon, iconHover, value }: IconProps) => {
  const [className, setClassName] = useState(icon);
  return (
    <div
      onMouseOver={() => setClassName(iconHover)}
      onMouseLeave={() => setClassName(icon)}
    >
      <i className={className}></i>
      &nbsp;&nbsp;
      {value}
    </div>
  );
};


const ModalWidget = ({
  isOpen,
  title,
  children,
  onClose,
  modalWidth,
  modalHeight,
  positionTopRight = false,
  styleObject = {},
  backgroundColor,
  textColor,
}: ModalWidgetProps) => {
  // Optimized modal width logic
  const maxComputedModalWidth = modalWidth ? modalWidth : { xs: '90%', sm: '80%', md: '50%', lg: '40%' };
  // Optimized modal height logic
  const maxComputedHeight = modalHeight ? modalHeight : { xs: '90%', sm: '90%', md: '90%', lg: '90%' };
  // Optimized position top right logic
  const computedPositionTopRight = positionTopRight
    ? { position: 'absolute' as const, top: '10vh', right: '10vw', margin: '7%' }
    : undefined;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [handleEscKey]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="keep-mounted-modal-title"
      disableAutoFocus
    >
      <Box
        sx={{
          ...style,
          ...styleObject,
          border: `1px solid ${theme.palette.prime.granite}`,
          width: modalWidth ? modalWidth : maxComputedModalWidth,
          maxWidth: maxComputedModalWidth,
          height: modalHeight ? modalHeight : 'fit-content',
          maxHeight: maxComputedHeight,
          overflow: 'auto',
          backgroundColor: backgroundColor || (isDarkMode ? theme.palette.background.default : theme.palette.background.paper),
          color: textColor,
          ...(computedPositionTopRight || {}),
        } as any}
      >
        <Stack
          direction="row"
          spacing={2}
          display="flex"
          justifyContent="space-between"
        >
          <Typography sx={{ p: 2, font: 'normal normal 600 18px/24px Open Sans' }}>
            {title}
          </Typography>
          <NavButton
            sx={{ color: theme.palette.prime.blue, fontSize: theme.typography.body1.body1 }}
            onClick={onClose}
          >
            <i className="fa-solid fa-circle-xmark"></i>&nbsp;&nbsp;Close
          </NavButton>
        </Stack>
        <Divider sx={{ backgroundColor: theme.palette.prime.granite }} />
        <Box>
          {children}
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalWidget;
